import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ExternalLink, FlaskConical, Database, Info } from 'lucide-react';
import { getLocale, t } from '../utils/i18n';
import { useCommunityDataSource } from '../utils/communityDataSource';
import { getActiveGroup } from '../header/navigationConfig';
import { useApiEnvironment } from '../context/ApiEnvironmentContext';
import { useDmPersona } from '../context/DmPersonaContext';
import { PAGE_CONTEXTS, getPageContext } from './pageContexts';

export function relatedContextLink(path, search, preserveScope) {
  if (!preserveScope) return path;
  const current = new URLSearchParams(search);
  const scope = new URLSearchParams();
  ['from', 'to', 'range', 'platform', 'version', 'appVersion', ...(path === '/landmarks' ? ['landmark'] : [])].forEach(key => {
    if (current.has(key)) scope.set(key, current.get(key));
  });
  const version = scope.get('version') || scope.get('appVersion');
  if (version) scope.set('version', version);
  scope.delete('appVersion');
  if (path === '/products') {
    const platform = scope.get('platform')?.toLowerCase();
    if (platform === 'all') scope.delete('platform');
    else if (platform === 'ios' || platform === 'android') scope.set('platform', platform === 'ios' ? 'iOS' : 'Android');
    path = '/products/story';
  }
  return scope.toString() ? `${path}?${scope}` : path;
}

export default function PageContext() {
  const location = useLocation();
  const { apiEnvironment } = useApiEnvironment();
  const communitySource = useCommunityDataSource();
  const persona = useDmPersona();
  const context = getPageContext(location.pathname, location.search);
  if (!context) return null;
  const locale = getLocale();
  const copy = context[locale] || context.en;
  const group = getActiveGroup(location.pathname, true, true, location.search);
  const isCommunity = location.pathname.startsWith('/community');
  const demo = context.demo || (isCommunity && communitySource === 'mock');
  const isQa = context.qa;
  const usesPersona = location.pathname.startsWith('/dm') || location.pathname.startsWith('/celeb-room') || location.pathname === '/game/rps/play';
  return <section className={`admin-page-context ${demo ? 'is-demo' : ''}`} aria-label={t('adminUi.context')}>
    <div className="admin-context-purpose"><Info size={15} aria-hidden="true" /><span>{copy.purpose}</span></div>
    <div className="admin-context-meta">
      {group && <span>{t(`header.navigation.${group.id}`)}</span>}
      {demo ? <span className="admin-demo-label"><Database size={14} aria-hidden="true" />{t('adminUi.mockData')}</span> : isCommunity ? <span><Database size={14} aria-hidden="true" />API · {apiEnvironment.toUpperCase()}</span> : isQa ? <span><FlaskConical size={14} aria-hidden="true" />{t('adminUi.qa')}</span> : null}
      {context.related?.map(path => {
        const related = PAGE_CONTEXTS.find(item => item.path === path);
        return related ? <Link key={path} to={relatedContextLink(path, location.search, context.preserveScope)}>{(related[locale] || related.en).title}</Link> : null;
      })}
      {context.doc && <a href={`/docs/${context.doc}`}><ExternalLink size={13} aria-hidden="true" />{t('adminUi.guide')}</a>}
      {usesPersona && <Link to="/dm/users">{t('header.user.dmPersona')}: {persona.isAuthenticated ? `${persona.username || ''} #${persona.userId}` : t('adminUi.personaUnset')}</Link>}
    </div>
    {context.unreleased && <p className="admin-context-warning">{copy.unreleasedWarning || t('adminUi.unreleasedWarning')}</p>}
    {demo && <p className="admin-context-warning">{t('adminUi.mockWarning')}</p>}
  </section>;
}
